import { Flex } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { BaseHeader } from 'components/header';
import { Link, Subtitle, Text, Title } from 'components/mvp-typography';
import { PatientInfoState } from 'features/patient/_utils/patient-info/patient-info-slice-types';
import PatientHomepageImg from 'img/patient-homepage.svg';
import {
  AssessmentIndexEntry,
  AssessmentType
} from 'lib/documents/patient-assessment/patient-assessment';
import { getFirstName } from 'lib/utils/string/normalise';
import { sortBy } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetPatientInfo } from '../_utils/patient-info/hook-context-patientinfo';

function AssessmentCompleteContent(props: {
  patientInfo: PatientInfoState;
  overrideAssessmentType?: AssessmentType;
}) {
  const lastAssessment:
    | Pick<AssessmentIndexEntry, 'assessmentType'>
    | undefined = useMemo(
    () => {
      if (props.overrideAssessmentType) {
        return {
          assessmentType: props.overrideAssessmentType
        } as Pick<AssessmentIndexEntry, 'assessmentType'>;
      }
      return sortBy(props.patientInfo.assessmentIndexes, a => a.date.unix()).at(
        0
      );
    },
    // eslint-disable-next-line
    [props.patientInfo.assessmentIndexes, props.overrideAssessmentType]
  );

  const firstName = getFirstName(props.patientInfo.patient.displayName ?? '');
  const TEXT_CONFIG: Record<
    string,
    (assessmentType: AssessmentType) => ReactNode
  > = {
    HERO_TEXT: assessmentType =>
      assessmentType === 'post-injury' ? (
        <>
          Your assessment is complete!
          <br style={{ marginBottom: '1rem' }} />
          Thank you for using ConcussionRx, {firstName}.{' '}
        </>
      ) : (
        'Congratulations on completing your ConcussionRx™ Baseline test!'
      ),
    subtext1: assessmentType =>
      assessmentType === 'post-injury'
        ? 'Your Physician or organisation now has access to your results, which they can access using your unique Test-Taker ID.'
        : `I've had a baseline test with ConcussionRx™, now what?`,
    subtext2: assessmentType =>
      assessmentType === 'post-injury' ? (
        <>
          Make an appointment with your Provider, so they can share your results
          and ConcussionRx recommendations with you.
        </>
      ) : (
        'Your results dashboard will be shared with the appropriate designated individuals (i.e., coach, healthcare provider, athlete, parent) within 2 weeks following ConcussionRx™ test completion.'
      ),
    footer1: assessmentType =>
      assessmentType === 'post-injury' ? (
        <>
          Consult your Provider to determine when you need to do your next
          assessment, to track your recovery progress over time.
          <br />
          See you soon!
        </>
      ) : (
        <>
          {`You can request a follow up appointment to discuss any findings or questions you may have.`}
        </>
      ),
    footer2: assessmentType =>
      assessmentType === 'post-injury' ? (
        <></>
      ) : (
        <>
          {`Just email us at `}
          <Link href={`mailto:${CRX_SUPPORT_EMAIL}`}>{CRX_SUPPORT_EMAIL}</Link>
          {` and we'll be in touch!`}
        </>
      )
  } as const;

  if (props.patientInfo.isLoading) return null;
  return (
    <Flex
      gap={40}
      justify="center"
      align="center"
      vertical
      style={{
        height: '80vh'
      }}
    >
      <Flex gap={16} vertical>
        <div>
          <Title color={'#230056'}>
            {TEXT_CONFIG.HERO_TEXT(
              lastAssessment?.assessmentType ?? 'post-injury'
            )}
          </Title>
        </div>
        <Flex vertical align="center">
          <Flex gap={16} style={{ marginBottom: '2rem' }}>
            <div
              style={{
                background: 'white',
                padding: '1.2rem',
                borderRadius: 5,
                maxWidth: 650,
                color: '#230056'
              }}
            >
              <Text>
                {TEXT_CONFIG.subtext1(
                  lastAssessment?.assessmentType ?? 'post-injury'
                )}
              </Text>
              <br></br>
              <br></br>
              <Text>
                {TEXT_CONFIG.subtext2(
                  lastAssessment?.assessmentType ?? 'post-injury'
                )}
              </Text>
            </div>
            <div>
              <img
                src={PatientHomepageImg}
                style={{
                  width: 320,
                  height: 225,
                  top: 373,
                  left: 1124,
                  borderRadius: 5
                }}
              />
            </div>
          </Flex>
          <div>
            <Subtitle
              style={{ color: '#230056', paddingBottom: '10px' }}
              className="crx-typography button-big"
            >
              {TEXT_CONFIG.footer1(
                lastAssessment?.assessmentType ?? 'post-injury'
              )}
            </Subtitle>
            <Subtitle color="#A366FC" className="crx-typography button-big">
              {TEXT_CONFIG.footer2(
                lastAssessment?.assessmentType ?? 'post-injury'
              )}
            </Subtitle>
          </div>
        </Flex>
      </Flex>
    </Flex>
  );
}

export function AssessmentComplete(props: {
  overrideAssessmentType?: AssessmentType;
}) {
  const { patientId } = useParams<{ patientId: string }>();
  const patientInfo = useGetPatientInfo(patientId);
  return (
    <div>
      <BaseHeader headerStyle={{ background: 'none' }} />
      <AssessmentCompleteContent
        patientInfo={patientInfo.state}
        overrideAssessmentType={props.overrideAssessmentType}
      />
    </div>
  );
}
